import { iLine, iOrder } from "/app/src/models";
import { Col, Row } from "antd";
import { useTranslation } from "react-i18next";
import { iLineService, iOrderService } from "/app/src/services";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buildParams } from "/app/src/helpers";
import Line from "./line";
import { handlePromiseError } from "/app/src/helpers/api";
import { useCallback } from "react";
import { Button } from "@heroui/react";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";

/**
 * Displays the details of a line in an order created through the Order Builder App
 */
export default function Lines({ order }: { order: iOrder }) {
  const { t } = useTranslation();
  const { data: lines } = useQuery({
    queryKey: ["lines", { orderId: order.id }],
    queryFn: () => {
      return iLineService.getAll(buildParams({ orderId: order.id }));
    },
    initialData: { lines: [] },
    select: (data: { lines: iLine[] }) => {
      return data.lines;
    },
    enabled: order?.id !== undefined,
  });
  const queryClient = useQueryClient();

  // Mutator that refreshes the order lines
  const { mutateAsync: refreshLines } = useMutation({
    mutationFn: () => {
      return iOrderService.refreshOrder(order.id).then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(["lines", { orderId: order.id }], {
        lines: response?.order?.lines,
      });
      queryClient.setQueryData(["order", order.id], response);
    },
  });

  const handleRefresh = useCallback(async () => {
    await refreshLines();
  }, [refreshLines]);

  if (lines.length > 0) {
    return (
      <>
        <div className="flex items-center justify-between pb-2">
          <h2>{t("translation:lines")}</h2>
          {order?.type === "Replenishment" && (
            <Button
              color="primary"
              radius="none"
              size="sm"
              className="text-[14px]"
              isDisabled={order.orderSent}
              onPress={handleRefresh}
              startContent={
                <IconBuilder icon="Refresh" color="#ffffff" size={16} />
              }
            >
              {t("translation:refresh_order_lines")}
            </Button>
          )}
        </div>

        <Row gutter={16}>
          {lines
            .sort((a, b) => a.id - b.id)
            .map((line) => (
              <Col span={24} key={line.id}>
                <Line
                  line={line}
                  key={line.id}
                  disabled={order.orderSent}
                  integrationId={order.integrationId}
                  onlyLine={lines.length === 1}
                />
              </Col>
            ))}
        </Row>
      </>
    );
  }
}
